import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' 
    ? (process.env.REACT_APP_API_URL || 'https://pdks.bytebridge.com.tr/api')
    : 'https://pdks.bytebridge.com.tr/api',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000, // 10 seconds timeout
  withCredentials: false // Changed to false to avoid CORS issues
});

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Add dummy token for development if no token exists
      config.headers.Authorization = `Bearer dummy-token`;
    }
    console.log(`Making ${config.method?.toUpperCase()} request to: ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    console.log(`Response received: ${response.status} from ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('Response interceptor error:', error);
    
    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401) {
      console.log('401 Unauthorized - clearing token and redirecting to login');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    // Handle network errors
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('Network error - server may be down or CORS issue');
      // Don't redirect on network error, let the component handle it
    }
    
    return Promise.reject(error);
  }
);

// Login API method
export const loginAPI = {
  login: async (email, password) => {
    try {
      console.log('Attempting login...');
      const response = await api.post('/login', {
        email,
        password,
      });
      return response.data;
    } catch (error) {
      console.error('Login API error:', error);
      if (error.response) {
        throw error.response.data;
      } else if (error.request) {
        throw { message: 'Network error - unable to reach server' };
      } else {
        throw { message: 'Request setup error' };
      }
    }
  }
};

// Profile API methods
export const profileAPI = {
  // Get user profile with retry mechanism
  getProfile: async (retries = 1) => {
    for (let i = 0; i <= retries; i++) {
      try {
        const response = await api.get('/employee/profile');
        return response;
      } catch (error) {
        // Only retry on 500 errors and if we have retries left
        if (error.response?.status === 500 && i < retries) {
          // Wait with exponential backoff: 1s
          const delay = 1000;
          console.log(`Profile API failed, retrying in ${delay}ms... (attempt ${i + 1}/${retries + 1})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        // For all other errors or no retries left, throw the error
        throw error;
      }
    }
  },
  
  // Update user profile
  updateProfile: (data) => api.put('/employee/profile', data),
  
  // Change password
  changePassword: (data) => api.put('/employee/change-password', data)
};

// Departments API
export const departmentsAPI = {
  getAll: () => api.get('/departments'),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`)
};

// Employees API
export const employeesAPI = {
  getAll: () => api.get('/employees'),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`)
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`)
};

// Leave Requests API
export const leaveRequestsAPI = {
  getAll: () => api.get('/leave-requests'),
  getById: (id) => api.get(`/leave-requests/${id}`),
  create: (data) => api.post('/leave-requests', data),
  update: (id, data) => api.put(`/leave-requests/${id}`, data),
  delete: (id) => api.delete(`/leave-requests/${id}`)
};

// Leave Entitlements API
export const leaveEntitlementsAPI = {
  getAll: () => api.get('/leave-entitlements'),
  getById: (id) => api.get(`/leave-entitlements/${id}`),
  create: (data) => api.post('/leave-entitlements', data),
  update: (id, data) => api.put(`/leave-entitlements/${id}`, data),
  delete: (id) => api.delete(`/leave-entitlements/${id}`)
};

// Leave Types API
export const leaveTypesAPI = {
  getAll: () => api.get('/leave-types'),
  getById: (id) => api.get(`/leave-types/${id}`),
  create: (data) => api.post('/leave-types', data),
  update: (id, data) => api.put(`/leave-types/${id}`, data),
  delete: (id) => api.delete(`/leave-types/${id}`)
};

// Meeting Rooms API
export const meetingRoomsAPI = {
  getAll: () => api.get('/meeting-rooms'),
  getById: (id) => api.get(`/meeting-rooms/${id}`),
  create: (data) => api.post('/meeting-rooms', data),
  update: (id, data) => api.put(`/meeting-rooms/${id}`, data),
  delete: (id) => api.delete(`/meeting-rooms/${id}`)
};

// Payrolls API
export const payrollsAPI = {
  getAll: () => api.get('/payrolls'),
  getById: (id) => api.get(`/payrolls/${id}`),
  create: (data) => api.post('/payrolls', data),
  update: (id, data) => api.put(`/payrolls/${id}`, data),
  delete: (id) => api.delete(`/payrolls/${id}`)
};

// Attendance API
export const attendanceAPI = {
  getAll: () => api.get('/attendance'),
  getById: (id) => api.get(`/attendance/${id}`),
  create: (data) => api.post('/attendance', data),
  update: (id, data) => api.put(`/attendance/${id}`, data),
  delete: (id) => api.delete(`/attendance/${id}`)
};

// Visitors API
export const visitorsAPI = {
  getAll: () => api.get('/visitors'),
  getById: (id) => api.get(`/visitors/${id}`),
  create: (data) => api.post('/visitors', data),
  update: (id, data) => api.put(`/visitors/${id}`, data),
  delete: (id) => api.delete(`/visitors/${id}`)
};

// Meal Menu API
export const mealMenuAPI = {
  getAll: (params = {}) => api.get('/api/meal-menu', { params }),
  getById: (id) => api.get(`/api/meal-menu/${id}`),
  create: (data) => api.post('/api/meal-menu', data),
  update: (id, data) => api.put(`/api/meal-menu/${id}`, data),
  delete: (id) => api.delete(`/api/meal-menu/${id}`)
};

// Cafeteria API
export const cafeteriaAPI = {
  getAll: (params = {}) => api.get('/api/cafeteria', { params }),
  getById: (id) => api.get(`/api/cafeteria/${id}`),
  create: (data) => api.post('/api/cafeteria', data),
  delete: (id) => api.delete(`/api/cafeteria/${id}`)
};

// Export configured axios instance as default
export default api;
